import React, { useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import Type from 'prop-types';
import { MobileStepper } from '@mui/material';
import styles from './Carousel.module.css';

const SWIPE_THRESHOLD = 50;

const Carousel = (props) => {
  const {
    currentSlide,
    slides,
    stepperProps,
    stepperClassName,
    slideClassName,
    slideImgClassName,
    onSlideChange,
  } = props;
  const slideCount = slides.length;
  const touchStartXRef = useRef(null);
  const touchDeltaXRef = useRef(0);
  const [isDragging, setDragging] = useState(false);

  const safeSlide = useMemo(() => {
    if (slideCount === 0) {
      return 0;
    }

    return Math.max(0, Math.min(currentSlide, slideCount - 1));
  }, [currentSlide, slideCount]);

  const handleSlideShift = (direction) => {
    if (slideCount <= 1) {
      return;
    }

    const nextSlide = Math.max(0, Math.min(safeSlide + direction, slideCount - 1));

    if (nextSlide !== safeSlide) {
      onSlideChange(nextSlide);
    }
  };

  const handleTouchStart = (event) => {
    touchStartXRef.current = event.touches[0].clientX;
    touchDeltaXRef.current = 0;
    setDragging(true);
  };

  const handleTouchMove = (event) => {
    if (touchStartXRef.current === null) {
      return;
    }

    touchDeltaXRef.current = event.touches[0].clientX - touchStartXRef.current;
  };

  const handleTouchEnd = () => {
    if (touchDeltaXRef.current <= -SWIPE_THRESHOLD) {
      handleSlideShift(1);
    } else if (touchDeltaXRef.current >= SWIPE_THRESHOLD) {
      handleSlideShift(-1);
    }

    touchStartXRef.current = null;
    touchDeltaXRef.current = 0;
    setDragging(false);
  };

  const handleMouseDown = (event) => {
    touchStartXRef.current = event.clientX;
    touchDeltaXRef.current = 0;
    setDragging(true);
  };

  const handleMouseMove = (event) => {
    if (!isDragging || touchStartXRef.current === null) {
      return;
    }

    touchDeltaXRef.current = event.clientX - touchStartXRef.current;
  };

  const handleMouseUp = () => {
    if (!isDragging) {
      return;
    }

    handleTouchEnd();
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      handleTouchEnd();
    }
  };

  return (
    <div className={styles.carousel}>
      <div
        className={styles.viewport}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className={styles.track}
          style={{ transform: `translateX(-${safeSlide * 100}%)` }}
        >
          {slides.map((slide) => (
            <div key={slide.id} className={cn(slideClassName, styles.slide)}>
              <img
                loading="lazy"
                className={cn(slideImgClassName, styles.slideImg)}
                src={slide.url}
                alt={slide.name}
                draggable={false}
              />
            </div>
          ))}
        </div>
      </div>

      <MobileStepper
        variant="text"
        steps={slideCount}
        position="static"
        activeStep={safeSlide}
        classes={{
          root: cn(stepperClassName, styles.stepper),
        }}
        nextButton={null}
        backButton={null}
        {...stepperProps}
      />
    </div>
  );
};

Carousel.propTypes = {
  currentSlide: Type.number.isRequired,
  onSlideChange: Type.func.isRequired,
  slides: Type.arrayOf(
    Type.shape({
      id: Type.oneOfType([Type.string, Type.number]).isRequired,
      name: Type.string.isRequired,
      url: Type.string.isRequired,
    }),
  ),
  stepperProps: Type.shape({
    position: Type.oneOf(['bottom', 'static', 'top']).isRequired,
    variant: Type.oneOf(['dots', 'progress', 'text']).isRequired,
  }),
  slideClassName: Type.string,
  slideImgClassName: Type.string,
  stepperClassName: Type.string,
};

Carousel.defaultProps = {
  slides: [],
  stepperProps: {
    position: 'static',
    variant: 'dots',
  },
  slideClassName: null,
  slideImgClassName: null,
  stepperClassName: null,
};

export default Carousel;
